import { FormEvent, useState } from "react"
import { Head, Link, router, useForm } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"
import { StarButton } from "@/components/StarButton"
import { UploadPlanSetsDialog } from "@/components/plan-sets/UploadPlanSetsDialog"
import { NewLotDialog } from "@/components/communities/NewLotDialog"
import { pluralize, relative } from "@/lib/relative-time"
import {
  unitWord,
  COMMUNITY_STATUSES,
  COMMUNITY_STATUS_LABELS,
  COMMUNITY_STATUS_TONES,
  STATUS_LABELS,
  STATUS_TONES,
  type CommunityStatus,
  type CommunityVocabulary,
  type ProjectStatus,
} from "@/lib/project-status"

type CommunityDetail = {
  id: number
  name: string
  kind: "community" | "building"
  status: CommunityStatus
  locality: string | null
  vocabulary: CommunityVocabulary
  starred: boolean
  customer: { id: number; name: string; customer_type: string }
}

type PlanCard = {
  id: number
  name: string
  elevation: string | null
  display_name: string
  heated_sf: number | null
  version: number
  published: boolean
  published_at: string | null
  room_count: number
  lots_count: number
  stale_count: number
  has_master: boolean
  sheets_ready: number
  starred: boolean
}

type LotRow = {
  id: number
  display_name: string
  address: string | null
  status: ProjectStatus
  source_version: number | null
  drifted: boolean
  mirrored: boolean
  room_count: number
  edited_since_copy: boolean
  updated_at: string
}

type LotGroup = {
  plan: { id: number; display_name: string; version: number }
  stale_count: number
  lots: LotRow[]
}

type PlanSetSummary = {
  id: number
  title: string
  status: string
  page_count: number | null
  picker_url: string
}

type BidSummary =
  | { exists: false; start_url: string }
  | {
      exists: true
      bid_number: string
      status: "draft" | "active" | "lost"
      rebid_needed: boolean
      blended_gm: string | null
      level_count: number
      synced_plans: number
      last_revision: { number: number; sent_at: string | null; generated_at: string } | null
      workspace_url: string
    }

type TemplatePin = {
  category: string
  label: string
  pinned_version: number | null
  current_version: number | null
  behind: boolean
}

type Props = {
  community: CommunityDetail
  plans: PlanCard[]
  lot_groups: LotGroup[]
  ungrouped_lots: LotRow[]
  plan_sets: PlanSetSummary[]
  bid_summary: BidSummary | null
  template_pins: TemplatePin[]
  templates_url: string
}

export default function CommunitiesShow({
  community,
  plans,
  lot_groups,
  ungrouped_lots,
  plan_sets,
  bid_summary,
  template_pins,
  templates_url,
}: Props) {
  const [tab, setTab] = useState<"library" | "lots" | "bid" | "template">("library")
  const vocab = community.vocabulary
  const totalLots = lot_groups.reduce((n, g) => n + g.lots.length, 0) + ungrouped_lots.length

  const description = `${vocab.model} library and ${vocab.units.toLowerCase()} for ${community.name}.`

  const changeStatus = (status: string) => {
    router.patch(`/communities/${community.id}`, { status }, { preserveScroll: true })
  }

  return (
    <>
      <Head title={community.name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={community.name} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <div className="text-sm text-ink-muted">
          {community.customer.name} <span className="text-ink-muted">/</span>{" "}
          <strong className="text-ink-display">{community.name}</strong>
        </div>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2">
              {community.name}
              <StarButton type="Community" id={community.id} starred={community.starred} />
              <Badge tone={COMMUNITY_STATUS_TONES[community.status]}>
                {COMMUNITY_STATUS_LABELS[community.status]}
              </Badge>
            </h1>
            <p className="mt-1 text-sm text-ink-muted">
              {[community.locality, community.customer.customer_type.replace("_", " ")]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Select
              aria-label="Bid status"
              value={community.status}
              onChange={(e) => changeStatus(e.target.value)}
              className="w-36"
            >
              {COMMUNITY_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {COMMUNITY_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
            <UploadPlanSetsDialog
              postUrl={`/communities/${community.id}/plan_sets`}
              trigger={<Button variant="secondary">Upload plan set</Button>}
            />
            <NewLotDialog communityId={community.id} vocabulary={vocab} plans={plans} />
          </div>
        </div>

        <div className="mt-6 flex gap-6 border-b border-hairline text-sm">
          {(
            [
              ["library", `${vocab.model} library`, plans.length],
              ["lots", vocab.units, totalLots],
              ["bid", "Bid & pricing", null],
              ["template", "Order template", null],
            ] as const
          ).map(([key, label, count]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`-mb-px border-b-2 pb-2.5 font-medium ${
                tab === key
                  ? "border-accent text-ink-display"
                  : "border-transparent text-ink-muted hover:text-ink-body"
              }`}
            >
              {label}
              {count != null && <span className="ml-1.5 font-normal text-ink-muted">{count}</span>}
            </button>
          ))}
        </div>

        {tab === "library" && (
          <LibraryTab community={community} plans={plans} planSets={plan_sets} />
        )}
        {tab === "lots" && (
          <LotsTab community={community} groups={lot_groups} ungrouped={ungrouped_lots} />
        )}
        {tab === "bid" && (
          <BidTab
            summary={bid_summary}
            container={vocab.container.toLowerCase()}
            unit={vocab.unit.toLowerCase()}
          />
        )}
        {tab === "template" && (
          <TemplateTab
            pins={template_pins}
            hasBid={bid_summary?.exists === true}
            templatesUrl={templates_url}
          />
        )}
      </AppShell>
    </>
  )
}

function LibraryTab({
  community,
  plans,
  planSets,
}: {
  community: CommunityDetail
  plans: PlanCard[]
  planSets: PlanSetSummary[]
}) {
  const vocab = community.vocabulary
  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-muted">
          <span aria-hidden>⚠</span> Masters are the bid drawings. Editing one does not change{" "}
          {vocab.units.toLowerCase()} already created from it — you choose when to publish.
        </p>
        <NewPlanDialog community={community} />
      </div>

      {planSets.length > 0 && (
        <p className="mt-3 text-sm text-ink-muted">
          Plan sets:{" "}
          {planSets.map((set, i) => (
            <span key={set.id}>
              {i > 0 && " · "}
              <Link href={set.picker_url}>{set.title}</Link>
              {set.status !== "ready" && <span className="text-ink-muted"> (processing…)</span>}
            </span>
          ))}
        </p>
      )}

      {plans.length === 0 ? (
        <div className="callout mt-4 max-w-lg">
          <p>
            No {vocab.model.toLowerCase()}s yet. Add one, upload the plan set, and draw its
            master takeoff once — every {vocab.unit.toLowerCase()} copies from it.
          </p>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <PlanCardView key={plan.id} community={community} plan={plan} />
          ))}
        </div>
      )}
    </div>
  )
}

function PlanCardView({ community, plan }: { community: CommunityDetail; plan: PlanCard }) {
  const vocab = community.vocabulary
  const [publishing, setPublishing] = useState(false)
  const publish = () => {
    setPublishing(true)
    router.post(
      `/communities/${community.id}/plans/${plan.id}/publish`,
      {},
      { preserveScroll: true, onFinish: () => setPublishing(false) },
    )
  }
  const openMaster = () => {
    router.post(`/communities/${community.id}/plans/${plan.id}/open_master`)
  }

  return (
    <div className="overflow-hidden rounded-lg border border-hairline bg-page">
      <div className="flex items-center justify-between border-b border-hairline bg-surface px-4 py-2">
        <span className="flex min-w-0 items-center gap-1">
          <span className="truncate font-medium text-ink-display">{plan.display_name}</span>
          <StarButton type="Plan" id={plan.id} starred={plan.starred} className="h-6 w-6" />
        </span>
        <span className="font-mono text-xs text-ink-muted">
          {plan.published ? `v${plan.version}` : "—"}
        </span>
      </div>
      <div className="space-y-3 px-4 py-3">
        <p className="text-sm text-ink-muted">
          {[
            plan.heated_sf ? `${plan.heated_sf.toLocaleString()} SF heated` : null,
            plan.room_count ? pluralize(plan.room_count, "room") : null,
          ]
            .filter(Boolean)
            .join(" · ") || "Not drawn yet"}
        </p>
        <div className="flex items-center justify-between">
          <Badge tone={plan.published ? "accent" : "neutral"}>
            {plan.published ? "Master ready" : "Not drawn"}
          </Badge>
          <span className="text-sm text-ink-muted">
            {plan.lots_count} {unitWord(vocab, plan.lots_count)}
            {plan.stale_count > 0 && (
              <span className="text-signal-display"> · {plan.stale_count} behind</span>
            )}
          </span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={openMaster} data-testid={`open-master-${plan.id}`}>
            {plan.has_master ? "Open master" : "Draw master"}
          </Button>
          {plan.sheets_ready > 0 && plan.has_master && (
            <Button size="sm" variant="ghost" onClick={publish} disabled={publishing}>
              {plan.published ? "Publish update" : "Publish"}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function NewPlanDialog({ community }: { community: CommunityDetail }) {
  const vocab = community.vocabulary
  const [open, setOpen] = useState(false)
  const form = useForm({ name: "", elevation: "", heated_sf: "" })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post(`/communities/${community.id}/plans`, {
      preserveScroll: true,
      onSuccess: () => {
        form.reset()
        setOpen(false)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">New {vocab.model.toLowerCase()}</Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>New {vocab.model.toLowerCase()}</DialogTitle>
            <DialogDescription>
              A {vocab.model.toLowerCase()} owns one master takeoff, drawn once during the bid.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor="plan-name">Name</label>
              <Input
                id="plan-name"
                value={form.data.name}
                onChange={(e) => form.setData("name", e.target.value)}
                placeholder={community.kind === "building" ? "A1" : "Cypress"}
                className="mt-1"
                autoFocus
              />
              {form.errors.name && <p className="mt-1 text-sm text-danger-display">{form.errors.name}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="plan-elevation">Elevation</label>
                <Input
                  id="plan-elevation"
                  value={form.data.elevation}
                  onChange={(e) => form.setData("elevation", e.target.value)}
                  placeholder="Elev. B"
                  className="mt-1"
                />
              </div>
              <div>
                <label htmlFor="plan-sf">Heated SF</label>
                <Input
                  id="plan-sf"
                  type="number"
                  value={form.data.heated_sf}
                  onChange={(e) => form.setData("heated_sf", e.target.value)}
                  placeholder="1842"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button type="button" variant="secondary">Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={form.processing || !form.data.name.trim()}>
              Add to library
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function LotsTab({
  community,
  groups,
  ungrouped,
}: {
  community: CommunityDetail
  groups: LotGroup[]
  ungrouped: LotRow[]
}) {
  const vocab = community.vocabulary
  const [q, setQ] = useState("")
  const match = (lot: LotRow) =>
    q.trim() === "" ||
    `${lot.display_name} ${lot.address ?? ""}`.toLowerCase().includes(q.trim().toLowerCase())

  const empty = groups.length === 0 && ungrouped.length === 0
  const visibleUngrouped = ungrouped.filter(match)

  return (
    <div className="mt-6">
      <div className="flex items-center gap-3">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${vocab.unit.toLowerCase()} #, address…`}
          className="max-w-xs"
          aria-label={`Search ${vocab.units.toLowerCase()}`}
        />
        <span className="text-sm text-ink-muted">
          Grouped by {vocab.model.toLowerCase()} so coverage reads at a glance.
        </span>
      </div>

      {empty && (
        <div className="callout mt-4 max-w-lg">
          <p>No {vocab.units.toLowerCase()} yet — create one from a published master.</p>
        </div>
      )}

      {groups.map((group) => {
        const visible = group.lots.filter(match)
        if (visible.length === 0 && q.trim() !== "") return null
        return (
          <div key={group.plan.id} className="mt-5 overflow-hidden rounded-lg border border-hairline">
            <div className="flex items-center gap-2 border-b border-hairline bg-surface px-4 py-2 text-sm">
              <span className="font-medium text-ink-display">{group.plan.display_name}</span>
              <span className="font-mono text-xs text-ink-muted">master v{group.plan.version}</span>
              <span className="text-ink-muted">· {group.lots.length} {unitWord(vocab, group.lots.length)}</span>
              <span className={`ml-auto ${group.stale_count > 0 ? "text-signal-display" : "text-accent-display"}`}>
                {group.stale_count > 0
                  ? `${group.stale_count} on older versions — review`
                  : "All current"}
              </span>
            </div>
            <ul className="divide-y divide-hairline bg-page">
              {visible.map((lot) => (
                <LotRowView key={lot.id} lot={lot} />
              ))}
            </ul>
          </div>
        )
      })}

      {visibleUngrouped.length > 0 && (
        <div className="mt-5 overflow-hidden rounded-lg border border-hairline">
          <div className="border-b border-hairline bg-surface px-4 py-2 text-sm font-medium text-ink-display">
            No {vocab.model.toLowerCase()}
          </div>
          <ul className="divide-y divide-hairline bg-page">
            {visibleUngrouped.map((lot) => (
              <LotRowView key={lot.id} lot={lot} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function LotRowView({ lot }: { lot: LotRow }) {
  return (
    <li className="grid grid-cols-[minmax(110px,1fr)_minmax(120px,1.3fr)_minmax(110px,1fr)_auto_auto_70px] items-center gap-x-3 px-4 py-2.5 text-sm hover:bg-surface">
      <Link
        href={`/projects/${lot.id}`}
        className="truncate font-medium text-ink-display no-underline hover:underline"
      >
        {lot.display_name}
      </Link>
      <span className="truncate text-ink-muted">{lot.address ?? "—"}</span>
      <span className={`truncate text-xs ${lot.drifted ? "text-signal-display" : "text-ink-muted"}`}>
        {lot.source_version != null
          ? `Copy of v${lot.source_version}${lot.drifted ? " ⚠" : ""}${lot.mirrored ? " · mirrored" : ""}`
          : "Own takeoff"}
      </span>
      {lot.drifted ? <RecopyDialog lot={lot} /> : <span />}
      <Badge tone={STATUS_TONES[lot.status]}>{STATUS_LABELS[lot.status]}</Badge>
      <span className="text-right text-xs text-ink-muted">{relative(lot.updated_at)}</span>
    </li>
  )
}

// Never a silent overwrite (spec §3): the confirm states exactly what is
// replaced and that a savepoint keeps it recoverable.
function RecopyDialog({ lot }: { lot: LotRow }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const recopy = () => {
    setBusy(true)
    router.post(
      `/projects/${lot.id}/recopy`,
      {},
      {
        preserveScroll: true,
        onFinish: () => {
          setBusy(false)
          setOpen(false)
        },
      },
    )
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`recopy-${lot.id}`}>
          Re-copy
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Re-copy {lot.display_name}?</DialogTitle>
          <DialogDescription>
            Replaces this takeoff with the current master — wholesale.{" "}
            {lot.room_count > 0 &&
              `Its ${lot.room_count} room${lot.room_count === 1 ? "" : "s"}${
                lot.edited_since_copy ? ", edited since the copy," : ""
              } will be replaced. `}
            The current state is saved as a savepoint first.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <DialogClose asChild>
            <Button variant="secondary">Cancel</Button>
          </DialogClose>
          <Button onClick={recopy} disabled={busy}>
            Re-copy from master
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** The Bid & pricing tab (M9): a live summary + the door into the bid
 *  workspace, or the start button. Null summary = not a production builder
 *  — those jobs are bid in FloorCore, and Measure only takes them off. */
function BidTab({
  summary,
  container,
  unit,
}: {
  summary: BidSummary | null
  container: string
  unit: string
}) {
  if (summary == null) {
    return (
      <div className="callout mt-6 max-w-lg">
        <p>
          Bidding in Measure is for production builders — retail and custom jobs are bid in
          FloorCore. This {container}&apos;s job here is takeoffs and exports.
        </p>
      </div>
    )
  }

  if (!summary.exists) {
    return (
      <div className="mt-6 max-w-lg space-y-4" data-testid="bid-tab-summary">
        <div className="callout">
          <p>
            No bid yet. The bid lives here — you bid a {container}, not a {unit}: pricing, the
            product lineup, and per-level quoted prices all attach at this level and every{" "}
            {unit} inherits them.
          </p>
        </div>
        <Button onClick={() => router.post(summary.start_url)} data-testid="start-bid">
          Start bid
        </Button>
      </div>
    )
  }

  return (
    <div className="mt-6 max-w-lg space-y-4" data-testid="bid-tab-summary">
      <div className="rounded-lg border border-hairline bg-surface p-4">
        <p className="flex items-center gap-2">
          <strong>{summary.bid_number}</strong>
          {summary.rebid_needed ? (
            <Badge tone="signal">⚠ Rebid — inputs changed</Badge>
          ) : (
            <Badge tone={summary.status === "active" ? "accent" : "neutral"}>
              {summary.status === "active" ? "Active" : summary.status === "lost" ? "Lost" : "Draft"}
            </Badge>
          )}
        </p>
        <dl className="mt-3 grid grid-cols-[10rem_1fr] gap-y-1 text-sm">
          <dt className="text-ink-muted">Blended GM</dt>
          <dd>{summary.blended_gm ? `${summary.blended_gm}%` : "—"}</dd>
          <dt className="text-ink-muted">Priced levels</dt>
          <dd>{summary.level_count}</dd>
          <dt className="text-ink-muted">Synced takeoffs</dt>
          <dd>{summary.synced_plans}</dd>
          <dt className="text-ink-muted">Last revision</dt>
          <dd>
            {summary.last_revision
              ? `Rev ${summary.last_revision.number} · ${relative(summary.last_revision.generated_at)}${
                  summary.last_revision.sent_at ? " · sent" : ""
                }`
              : "None generated"}
          </dd>
        </dl>
      </div>
      <Button asChild data-testid="open-bid-workspace">
        <Link href={summary.workspace_url}>Open bid workspace →</Link>
      </Button>
    </div>
  )
}

/** The Order template tab (M9): templates are GLOBAL — this shows which
 *  frozen version each of this bid's categories pins. The link carries the
 *  community so the templates page can lead back to this workspace. */
function TemplateTab({
  pins,
  hasBid,
  templatesUrl,
}: {
  pins: TemplatePin[]
  hasBid: boolean
  templatesUrl: string
}) {
  return (
    <div className="mt-6 max-w-lg space-y-4" data-testid="template-pins">
      <div className="callout">
        <p>
          Order templates are company-global and versioned — one per job type. A bid pins a
          published version per category; publishing a new version never repoints a bid on its
          own.
        </p>
      </div>
      {hasBid && pins.length > 0 && (
        <ul className="divide-y divide-hairline rounded-lg border border-hairline bg-surface">
          {pins.map((pin) => (
            <li key={pin.category} className="flex items-center gap-2 px-4 py-2 text-sm">
              <span className="text-ink-display">{pin.label}</span>
              <span className="ml-auto text-ink-muted">
                {pin.pinned_version ? `pinned v${pin.pinned_version}` : "no template"}
              </span>
              {pin.behind && <Badge tone="signal">v{pin.current_version} available</Badge>}
            </li>
          ))}
        </ul>
      )}
      <Button asChild variant="secondary">
        <Link href={templatesUrl}>Open order templates →</Link>
      </Button>
    </div>
  )
}
